import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Globe, Filter, BarChart3, PieChart as PieIcon, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = [
  '#1E40AF', '#2563EB', '#D97706', '#059669', '#DC2626',
  '#7C3AED', '#0891B2', '#DB2777', '#65A30D',
];

const PERIOD_OPTIONS = [
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي' },
  { value: 'custom', label: 'تاريخ مخصص' },
];

const today = () => new Date().toISOString().split('T')[0];

export default function SalesByCountry() {
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [chartType, setChartType] = useState('bar');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const res = await axios.get(`${API_URL}/api/analytics/sales-by-country?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate]);

  useEffect(() => {
    if (period !== 'custom') fetchData();
  }, [period, fetchData]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === 'custom') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const res = await axios.get(
        `${API_URL}/api/analytics/sales-by-country/export/${format}?${params.toString()}`,
        { responseType: 'blob' }
      );
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_by_country_${period}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`تم تصدير ${format === 'excel' ? 'Excel' : 'PDF'} بنجاح`);
    } catch (err) {
      toast.error('فشل التصدير');
    } finally {
      setExporting(false);
    }
  };

  const chartData = (data?.by_country || []).map((c) => ({
    ...c,
    name: c.country,
    value: c.total,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-border rounded-lg shadow-lg p-3 text-right" dir="rtl">
        <p className="font-bold text-brand_blue mb-1">{d.country}</p>
        <p className="text-sm">
          <span className="text-foreground/60">الإجمالي: </span>
          <span className="font-bold font-numbers">{d.total.toLocaleString('en-US')} </span>
          <SAR />
        </p>
        <p className="text-sm">
          <span className="text-foreground/60">المعاملات: </span>
          <span className="font-bold font-numbers">{d.count}</span>
        </p>
        <p className="text-sm">
          <span className="text-foreground/60">النسبة: </span>
          <span className="font-bold font-numbers">{d.percentage}%</span>
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="sales-by-country-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
            <Globe className="h-6 w-6 text-brand_blue" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-foreground">المبيعات حسب الدولة</h1>
            <p className="text-sm text-foreground/60 mt-1">تحليل توزيع المبيعات على الدول ضمن الفترة المحددة</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="sbc-export-excel-btn"
            onClick={() => handleExport('excel')}
            variant="outline"
            className="gap-2 border-success/40 text-success hover:bg-success/10"
            disabled={loading || exporting}
          >
            <FileSpreadsheet className="h-4 w-4" />
            {exporting ? 'جاري التصدير...' : 'تصدير Excel'}
          </Button>
          <Button
            data-testid="sbc-export-pdf-btn"
            onClick={() => handleExport('pdf')}
            variant="outline"
            className="gap-2 border-destructive/40 text-destructive hover:bg-destructive/10"
            disabled={loading || exporting}
          >
            <FileText className="h-4 w-4" />
            {exporting ? 'جاري التصدير...' : 'تصدير PDF'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-brand_blue" />
          <h3 className="font-bold">الفترة الزمنية</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-2">
            <Label>النوع</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="country-period-select" className="text-right">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {period === 'custom' && (
            <>
              <div className="space-y-2">
                <Label>من تاريخ</Label>
                <Input
                  data-testid="country-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-right font-numbers"
                />
              </div>
              <div className="space-y-2">
                <Label>إلى تاريخ</Label>
                <Input
                  data-testid="country-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-right font-numbers"
                />
              </div>
              <Button
                data-testid="country-apply-filter"
                onClick={fetchData}
                className="gap-2"
                disabled={loading}
              >
                <Filter className="h-4 w-4" />
                {loading ? 'جاري التحميل...' : 'تطبيق'}
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Stats summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <p className="text-xs text-foreground/60 mb-1">الفترة</p>
          <p className="text-2xl font-black text-brand_blue">{data?.period_label || '—'}</p>
        </Card>
        <Card className="p-5">
          <p className="text-xs text-foreground/60 mb-1">إجمالي المبيعات</p>
          <p className="text-2xl font-black text-success font-numbers">
            {(data?.total || 0).toLocaleString('en-US')} <SAR />
          </p>
        </Card>
        <Card className="p-5">
          <p className="text-xs text-foreground/60 mb-1">عدد المعاملات</p>
          <p className="text-2xl font-black text-foreground font-numbers">{data?.transactions || 0}</p>
        </Card>
      </div>

      {/* Chart */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            {chartType === 'bar' ? <BarChart3 className="h-5 w-5 text-brand_blue" /> : <PieIcon className="h-5 w-5 text-brand_blue" />}
            الرسم البياني
          </h3>
          <div className="flex gap-2">
            <Button
              data-testid="chart-bar-btn"
              size="sm"
              variant={chartType === 'bar' ? 'default' : 'outline'}
              onClick={() => setChartType('bar')}
              className="gap-1.5"
            >
              <BarChart3 className="h-4 w-4" />
              أعمدة
            </Button>
            <Button
              data-testid="chart-pie-btn"
              size="sm"
              variant={chartType === 'pie' ? 'default' : 'outline'}
              onClick={() => setChartType('pie')}
              className="gap-1.5"
            >
              <PieIcon className="h-4 w-4" />
              دائري
            </Button>
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="text-center py-16 text-foreground/40">
            <Globe className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>لا توجد بيانات في هذه الفترة</p>
          </div>
        ) : (
          <div style={{ width: '100%', height: 380 }}>
            <ResponsiveContainer>
              {chartType === 'bar' ? (
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#374151' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" radius={[8, 8, 0, 0]}>
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="total"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={130}
                    innerRadius={60}
                    label={(e) => `${e.percentage}%`}
                    labelLine={false}
                  >
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" />
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="border-border">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="country-table">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-right py-3 px-4 text-sm font-bold">#</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الدولة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الإجمالي</th>
                <th className="text-right py-3 px-4 text-sm font-bold">المعاملات</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الكمية</th>
                <th className="text-right py-3 px-4 text-sm font-bold">النسبة</th>
              </tr>
            </thead>
            <tbody>
              {chartData.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">لا توجد بيانات</td></tr>
              ) : (
                chartData.map((c, i) => (
                  <tr key={c.country} className="border-t border-border hover:bg-accent/30">
                    <td className="py-3 px-4 font-numbers text-foreground/50">{i + 1}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-2 font-bold">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        {c.country}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-success font-bold font-numbers">
                      {c.total.toLocaleString('en-US')} <SAR />
                    </td>
                    <td className="py-3 px-4 font-numbers">{c.count}</td>
                    <td className="py-3 px-4 font-numbers">{c.quantity}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden min-w-[60px]">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${c.percentage}%`, backgroundColor: COLORS[i % COLORS.length] }}
                          />
                        </div>
                        <span className="font-numbers text-xs font-bold">{c.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
